/**
 * useProjectAnalysis Hook
 * Manages project analysis state with automatic staleness detection.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  projectAnalysisApi,
  type ProjectAnalysis,
  type ProjectInsights,
} from '@/services/projectAnalysisApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useProjectAnalysis');

export interface UseProjectAnalysisReturn {
  /** The analysis data (null if not yet analyzed) */
  analysis: ProjectAnalysis | null;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether refresh is in progress */
  isRefreshing: boolean;
  /** Whether the cached analysis is stale */
  isStale: boolean;
  /** Error message if any operation failed */
  error: string | null;
  /** Timestamp of last analysis */
  lastUpdated: string | null;
  /** Number of documents in the project */
  documentCount: number;
  /** Trigger manual refresh */
  refresh: () => Promise<void>;
}

export function useProjectAnalysis(): UseProjectAnalysisReturn {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const [analysis, setAnalysis] = useState<ProjectAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch analysis when authenticated
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setIsLoading(false);
      setAnalysis(null);
      return;
    }

    const fetchAnalysis = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await projectAnalysisApi.getAnalysis();
        setAnalysis(result);
        log.info('Analysis loaded', { exists: !!result });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load analysis';
        setError(message);
        log.error('Failed to load analysis', err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [isAuthenticated, authLoading]);

  // Refresh handler
  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setError('You must be signed in to analyze your project');
      return;
    }

    setIsRefreshing(true);
    setError(null);

    try {
      const result = await projectAnalysisApi.refreshAnalysis();
      setAnalysis(result);
      log.info('Analysis refreshed', {
        documentCount: result.documentCount,
        decisionsCount: result.insights?.metrics?.decisionsCount,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh analysis';
      setError(message);
      log.error('Failed to refresh analysis', err as Error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isAuthenticated]);

  return {
    analysis,
    isLoading,
    isRefreshing,
    isStale: analysis?.isStale ?? false,
    error,
    lastUpdated: analysis?.analyzedAt ?? null,
    documentCount: analysis?.documentCount ?? 0,
    refresh,
  };
}

// Re-export types for convenience
export type { ProjectAnalysis, ProjectInsights };
export default useProjectAnalysis;
